import { useState, useEffect } from "react";
import { PsnsRedriveApiFactory } from "../../../lusca-search-api/LuscaSearchApiFactory";
import {
  Stage,
  PsnsRedriveInfoResult,
} from "../../../lusca-search-api/generated-src";

const POLL_INTERVAL_MS = 2000;
// Consecutive failed polls before giving up, so a persistent error (expired
// token, backend down) doesn't poll forever.
const MAX_POLL_FAILURES = 5;

export const usePsnsRedriveDetails = (
  stage: Stage,
  executionArn: string | null
) => {
  const [details, setDetails] = useState<PsnsRedriveInfoResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    // Leaving the execution view clears its result and any poll error.
    if (!executionArn) {
      setDetails(null);
      setError("");
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    let polling = false;
    let failures = 0;

    // Poll while PENDING (the step function runs async); stop on COMPLETED/ERROR.
    // A failed poll keeps the last result and retries, so a transient error
    // doesn't end the auto-refresh; MAX_POLL_FAILURES in a row stops it.
    const fetchDetails = async (isInitial: boolean) => {
      try {
        if (isInitial) {
          setLoading(true);
        }
        setError("");

        const response = await PsnsRedriveApiFactory().getPsnsRedriveInfo(
          stage,
          executionArn
        );

        if (cancelled) {
          return;
        }

        setDetails(response.data);
        failures = 0;
        polling = response.data?.status === "PENDING";
      } catch (fetchError) {
        if (cancelled) {
          return;
        }
        console.error("Error fetching PSNS redrive details:", fetchError);
        setError("Failed to load PSNS redrive details");
        if (!polling) {
          setDetails(null);
        }
        failures += 1;
        if (failures >= MAX_POLL_FAILURES) {
          polling = false;
        }
      }
      if (polling) {
        pollTimer = setTimeout(() => {
          fetchDetails(false).catch(console.error);
        }, POLL_INTERVAL_MS);
      } else {
        setLoading(false);
      }
    };

    fetchDetails(true).catch(console.error);

    return () => {
      cancelled = true;
      if (pollTimer) {
        clearTimeout(pollTimer);
      }
    };
  }, [stage, executionArn]);

  return { details, loading, error };
};
